import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Fade } from "@mui/material";
import { keyframes } from "@emotion/react";

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const CountdownLoader = ({ initialSeconds = 15, onComplete = () => {} }) => {
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (seconds <= 0) {
      onComplete();
      return;
    }

    const timer = setTimeout(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [seconds, onComplete]);

  const progress = ((initialSeconds - seconds) / initialSeconds) * 100;

  const getProgressColor = () => {
    if (progress < 33) return "#4caf50";
    if (progress < 66) return "#ff9800";
    return "#f44336";
  };

  return (
    <Fade in={true}>
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 3,
          borderRadius: 4,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(8px)",
          animation: `${pulse} 2s infinite ease-in-out`,
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={100}
            thickness={5}
            sx={{
              color: getProgressColor(),
              transition: "color 0.5s ease",
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
            }}
          />
          <CircularProgress
            variant="determinate"
            value={100}
            size={100}
            thickness={5}
            sx={{
              color: "rgba(0, 0, 0, 0.1)",
              position: "absolute",
              left: 0,
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: "bold",
                color: getProgressColor(),
                transition: "color 0.5s ease",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
            >
              {seconds}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            fontWeight: "medium",
            letterSpacing: 0.5,
            color: "#424242",
          }}
        >
          Analyzing your crop image...
        </Typography>
        <Box
          sx={{
            mt: 1,
            width: "180px",
            height: "4px",
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${progress}%`,
              height: "100%",
              backgroundColor: getProgressColor(),
              transition: "width 1s ease, background-color 0.5s ease",
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default CountdownLoader;
